import React, { useState, useRef, useEffect } from 'react';
import './Course.css';
import { useParams, useHistory } from 'react-router-dom';
import { Input, Button, Progress, Alert, Select, Radio } from 'antd';
import { Form } from 'antd';
import {createSandbox, getCloudRedirect, tenantAppUrl} from './sandbox';
import { Navbar } from './Navbar';
import { Header } from './Header';
import Iframe from 'react-iframe';
import * as config from './config';
import {ICourseInfo} from "./Home";
import useSWR, {preload} from "swr";
import {getCallbackOrigin} from "./cloudSandboxUtil";


const apiUrl = '/api/v1/sandbox';
const fetcher = (url: string) => fetch(url).then((res) => res.json());
preload('/api/v1/sandbox/courses.json', fetcher);

// Define a proper interface for UTM parameters
interface UtmParameters {
  utm_source: string;
  utm_medium: string; 
  utm_campaign: string;
  utm_content: string | null;
  utm_term: string | null;
}

// Default UTM parameter values
const DEFAULT_UTM_PARAMS: UtmParameters = {
  utm_source: 'direct',
  utm_medium: 'website',
  utm_campaign: 'sandbox',
  utm_content: null,
  utm_term: null
};

// Extract UTM parameters with proper typing
const extractUtmParams = (url: string): UtmParameters => {
  try {
    const urlObj = new URL(url);
    
    // Create a new object with default values
    return {
      utm_source: urlObj.searchParams.get('utm_source') || DEFAULT_UTM_PARAMS.utm_source,
      utm_medium: urlObj.searchParams.get('utm_medium') || DEFAULT_UTM_PARAMS.utm_medium,
      utm_campaign: urlObj.searchParams.get('utm_campaign') || DEFAULT_UTM_PARAMS.utm_campaign,
      utm_content: urlObj.searchParams.get('utm_content'),
      utm_term: urlObj.searchParams.get('utm_term')
    };
  } catch (error) {
    console.warn('Error extracting UTM parameters:', error);
    // Return default UTM parameters if there's an error
    return {...DEFAULT_UTM_PARAMS};
  }
};

// Function to append UTM parameters to a URL string with proper typing
const appendUtmParamsToUrl = (baseUrl: string, utmParams: UtmParameters): string => {
  try {
    const url = new URL(baseUrl);
    
    // Add all non-null parameters
    Object.entries(utmParams).forEach(([key, value]) => {
      if (value !== null) {
        url.searchParams.append(key, value);
      }
    });
    
    return url.toString();
  } catch (error) {
    console.warn('Error appending UTM parameters to URL:', error);
    return baseUrl; // Return original URL as fallback
  }
};

export const Course = (props:any) => {
  const { data, isLoading } = useSWR<ICourseInfo[]>(`${apiUrl}/courses.json`, fetcher);
  const qs = new URLSearchParams(window.location.search);
  let { id } = useParams<{id: string}>();
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<Error|null>(null);
  const [email, setEmail] = useState<string|null>(qs.get('email'));
  const DEFAULT_TTL_IN_SECONDS = '86400';
  const [ttl, setTtl] = useState<string>(qs.get('ttl') || DEFAULT_TTL_IN_SECONDS);
  const [verificationCode, setVerificationCode] = useState<string|null>(qs.get('code'));
  const [starting, setStarting] = useState<boolean>(false);
  const [criblClientId, setCriblClientId] = useState<string>('');
  const [criblClientSecret, setCriblClientSecret] = useState<string>('');
  const [criblOrgId, setCriblOrgId] = useState<string>('');
  const [criblWorkspaceId, setCriblWorkspaceId] = useState<string>('');
  const [environment, setEnvironment] = useState<'staging' | 'production'>('production');
  const [engineId, setEngineId] = useState<string>('');
  const [hecToken, setHecToken] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const fromDemo = config.demoHosts.includes(window.location.hostname);

  const [form] = Form.useForm();
  const refHostGet = (): string => {
    // console.log(`Doc: ${JSON.stringify(document)}`);

    if ( document?.referrer ) {
      const referrer = new URL(document.referrer.toString())

      //const referer = new URL(req.headers.referer.toString());
      return referrer.hostname;
    } else if ( window.location?.hostname ) {
      return window.location.hostname;
    } else {
      return "localtest.me";
    }
  }
  const refHost = refHostGet();

  // define startSandbox() for later use...
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const startSandbox = async () => {
    console.log(`RefHost: ${refHost}`);
    let localProgress = 0;
    const updateProgress = setInterval(() => {
      localProgress = localProgress + 100/sec;
      setProgress(localProgress);
    }, 1000);
    let host;
    let cloudUrl: string | undefined;

    // Extract UTM parameters at the beginning so it's available for both paths
    const utmParams = extractUtmParams(window.location.href);
  
    try {
      if (course?.cloud === true) {
        const criblCloudHost: string = await getCloudRedirect(id);
        const callbackOrigin: string = getCallbackOrigin(window.location.origin);
        
        // Create a callback URL with UTM parameters
        const baseCallbackUrl = `${callbackOrigin}/callback`;
        const callbackUrlWithUtm = appendUtmParamsToUrl(baseCallbackUrl, utmParams);
        
        // Create the redirect URL with encoded callback
        const redirectUrl = `${criblCloudHost}/sandboxes/${course.id}/redirect?sandboxCallbackUrl=${encodeURIComponent(callbackUrlWithUtm)}`;
        
        window.location.href = redirectUrl;
        return;
      } else if (course?.provider?.platform === 'tenant') {
        host = await createSandbox(email || '', id, sec, verificationCode || '', ttl, refHost, {
          criblClientId, criblClientSecret, criblOrgId, criblWorkspaceId, environment,
          ...(engineId ? {engineId} : {}),
          ...(hecToken ? {hecToken} : {})
        });
        cloudUrl = tenantAppUrl(criblOrgId, criblWorkspaceId, environment);
      } else {
        host = await createSandbox(email || '', id, sec, verificationCode || '', ttl, refHost);
      }
    } catch (err) {
      clearInterval(updateProgress);
      if (err instanceof Error) {
        setError(err);
      }
      setProgress(0);
      setStarting(false);
      return;
    }
    
    history.push(`/ui/`, {
      id,
      host,
      email,
      course,
      ttl,
      cloudUrl,
      ...utmParams // Add UTM parameters to history state
    });
  }
  
  if (email) {
    form.setFieldsValue({email});
  }

  if (fromDemo && ttl) {
    form.setFieldsValue({ttl});
  }

  if (verificationCode) {
    form.setFieldsValue({verificationCode});
  }

  const course = data?.find(c => c.id===id);

  useEffect(() => {
    if (isLoading) {
      return
    }
    // We must use a somewhat hacky method of kicking off the API call to avoid creating side effects
    // We validate that everything has been filled out and set using set* hook methods
    // Then if they're all set properly, we will kick off startSandbox()

    // 404 handling, send the user to the home page
    if (!course) {
      history.replace("/");
    }

    // Special case to redirect users
    // Clicking the back button will take to previous page without knowing the course id was invalid
    if (course?.type === 'redirect') {
      history.replace(course.target as string);
    }

    // Email set, haven't started and we're not errored
    if (starting && (email || course?.cloud) && !error) {
      setStarting(false);
      const ttlSet = (fromDemo && ttl) || !fromDemo; // ttl only gets set on the demo page
      const verificationCodeSet = verificationCode || fromDemo // Verification Code only gets set on sandbox page
      const skipValidation = config.noValidateTypes.includes(course?.type || '') || config.noValidateSites.includes(refHost) || course?.cloud // CCOE and other sites skip all the form fields

      // Course page not from demo and no verification code filled out
      if (!fromDemo && !verificationCode && !skipValidation) {
        fetch(`${apiUrl}/requesttoken`, {
          method: 'POST',
          mode: 'cors',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        })
        .then(response => {
          if (!response.ok) {
            return response.text().then(errText => { throw new Error(errText) });
          }
        })
        .catch(err => { 
          if (err instanceof Error) {
            setError(err);
            setStarting(false);
          }
        });
      // From Demo and TTL selected or no TTL selected on course page
      // Verification Code is filled out or we're in the Demo page
      // Certain course types or certain sites we don't even care what's filled out
      } else if ((ttlSet && verificationCodeSet) || skipValidation) {
        startSandbox();
      }
    }
  }, [email, error, starting, fromDemo, ttl, verificationCode, course, refHost, startSandbox, history, isLoading]);

  const sec = course?.startupTime|| 180;
  const min = Math.floor(sec/60);
  const isTenantCourse = course?.provider?.platform === 'tenant';
  const requiresEngineId = isTenantCourse && course?.provider?.requiresEngineId === true;
  const requiresHecToken = isTenantCourse && course?.provider?.requiresHecToken === true;

  const submit = (values: any) => {
    if (values.email) {
      setEmail(values.email);
    }
    if (values.verificationCode) {
      setVerificationCode(values.verificationCode);
    }
    if (fromDemo && values.ttl) {
      setTtl(values.ttl);
    }
    if (isTenantCourse) {
      setCriblClientId(values.criblClientId || '');
      setCriblClientSecret(values.criblClientSecret || '');
      setCriblOrgId(values.criblOrgId || '');
      setCriblWorkspaceId(values.criblWorkspaceId || '');
      setEnvironment(values.environment || 'production');
      setEngineId((values.engineId || '').trim());
      setHecToken((values.hecToken || '').trim());
    }
    setError(null);
    setProgress(0);
    setStarting(true);
  };
  const validateMessages = {
    // eslint-disable-next-line no-template-curly-in-string
    required: '${label} is required!',
    types: {
      email: 'Please enter a valid email address.',
    }
  };

  let buttonText = 'Get Verification Code';

  if (email || config.noValidateTypes.includes(course?.type || '') || config.noValidateSites.includes(refHost) || course?.cloud) {
    buttonText = (fromDemo ? 'Start Demo' : 'Start Sandbox');
  }

  const ttlopts = [
    { value: '28800', label: "8 Hours"},
    { value: '43200', label: "12 Hours"},
    { value: '86400', label: "1 day"},
    { value: '604800', label: "1 week"}
  ];

  return <div ref={containerRef} className="course__container">
    <Navbar />
    <Header
      backUrl="/"
      backLabel={ fromDemo ? "Back to Demos" : "Back to Sandboxes"} 
      description={
        <div dangerouslySetInnerHTML={{ __html: course?.description || '' }} />
      }
      imageUrl={course?.imageUrl}
      imageWidth={course?.imageWidth}

    >
      <Form form={form} onFinish={submit} layout={isTenantCourse ? 'vertical' : 'inline'} className="course__form" name="sandbox" validateMessages={validateMessages}
          initialValues={{ email: qs.get('email'), ttl, verificationCode: qs.get('code'), environment: 'production' }}>
        { isTenantCourse &&
        <fieldset style={{border: '1px solid #d9d9d9', borderRadius: 8, padding: '24px 24px 16px', marginBottom: 16, background: '#fff'}}>
          <legend style={{fontSize: 12, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: '#1a2a3a', padding: '0 8px', marginLeft: -8, background: '#fff', borderRadius: 4}}>Cribl Cloud Credentials</legend>
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px'}}>
            <Form.Item name={'criblClientId'} label="Cribl Client ID" rules={[{required: true}]}>
              <Input placeholder="cribl_client_id" />
            </Form.Item>
            <Form.Item name={'criblClientSecret'} label="Cribl Client Secret" rules={[{required: true}]}>
              <Input.Password placeholder="cribl_client_secret" />
            </Form.Item>
            <Form.Item name={'criblOrgId'} label="Cribl Org ID" rules={[{required: true}]}>
              <Input placeholder="cribl_org_id" />
            </Form.Item>
            <Form.Item name={'criblWorkspaceId'} label="Cribl Workspace Name" rules={[{required: true}]}
              extra="The workspace slug from your Cribl.Cloud URL (usually 'main').">
              <Input placeholder="main" />
            </Form.Item>
          </div>
          { requiresEngineId &&
          <Form.Item name={'engineId'} label="LocalSearch Engine ID" rules={[{required: true, whitespace: true}]}
            extra="The id of an existing LocalSearch engine in your tenant. It will not be created for you.">
            <Input placeholder="my-engine" />
          </Form.Item>}
          { requiresHecToken &&
          <Form.Item name={'hecToken'} label="HEC Token" rules={[{required: true, whitespace: true}]}
            extra="HEC token for the Splunk HEC source in your tenant. Used by data generators to ship synthetic logs.">
            <Input.Password placeholder="hec_xxxxxxxxxxxxxxxx" />
          </Form.Item>}
          <Form.Item name={'environment'} label="Environment" style={{marginBottom: 16}}>
            <Radio.Group>
              <Radio.Button value="staging">Staging</Radio.Button>
              <Radio.Button value="production">Production</Radio.Button>
            </Radio.Group>
          </Form.Item>
          { fromDemo &&
          <Form.Item name={'ttl'} label="Data Generator Lifetime" style={{marginBottom: 0}}>
            <Select options={ttlopts} />
          </Form.Item>}
        </fieldset>
        }
        { !course?.cloud &&
        <Form.Item name={'email'} label="Email address" rules={[{ pattern: /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$|^\w+-\w+-\w+-\w+-\w+/, required: true, message: "Please enter a valid email address."}]}>
          <Input style={{width:300, marginRight:5, marginBottom: 10}} placeholder="Email address" name={"email"} value={email || ''}/>
        </Form.Item>
        }
        { fromDemo && !isTenantCourse &&
        <Form.Item name={'ttl'} label="Time to Live">
          <Select options={ttlopts} />
        </Form.Item>}
        {
          !isTenantCourse && email && ! config.noValidateTypes.includes(course?.type || '') && !config.noValidateSites.includes(refHost) && !course?.cloud &&
          <Form.Item name={'verificationCode'} label="Verification Code">
            <Input style={{width:300, marginRight:5, marginBottom: 10}} placeholder="Verification code" value={verificationCode || ''}/>
          </Form.Item>
        }
        <Form.Item>
          <Button type="primary" htmlType="submit" disabled={ progress===0 && error != null }>{isTenantCourse ? 'Provision Demo' : buttonText}</Button>
        </Form.Item>
      </Form>
      { 
        (email && !verificationCode && !error  && ! config.noValidateTypes.includes(course?.type || '')) && !config.noValidateSites.includes(refHost) && !course?.cloud &&
        <div className="course__verificationCode">Check your email for a verification code!</div>
      }
      {
        progress>0 &&
        <div className="course__progress">
          <span>{fromDemo ? 'Demo' : 'Sandbox'} starting... Expected time: {min} minutes</span>
          <Progress percent={Math.round(progress)} status="active" />
        </div>
      }
      {
        error &&
        <Alert type="error" showIcon message={fromDemo ? `Error occurred while creating demo: ${error.message}` : `Error occurred while creating sandbox: ${error.message}`} />
      }
    </Header>
    <div id='previewheader' className='wrapped'><h1>Course Preview</h1></div>
    <div id='iframeContainer'>
      <Iframe url={`/coursedocs/${course?.id}/docs/intro1`}
        id="docsIframe"
        height="100%"
        width="100%"
      />
    </div>
  </div>;
}
